<template>
	<BaseLayout :footer-full="true" :include-padding="false" :transparent-header="true" :is-venture="true">
		<AssetPageHero
			:hero="assetHeroConfig"
			:form-config="leadCaptureFormConfig"
			:offering-id="growthTechReitId"
			class="pt-300"
		/>
		<section v-if="isPartnerPage" class="bg-color-content-primary">
			<BasePlaceHolder class="container pb-200" :placehold="loading" theme="dark" :line-count="3">
				<SolicitorNotice
					data-test="venture-solicitor-notice"
					:solicitor="solicitorNoticeDetails.name"
					:payment="solicitorNoticeDetails.payment"
					:is-cpa-partner="solicitorNoticeDetails.isCpaPartner"
					:is-investor="solicitorNoticeDetails.isInvestor"
					:monthly-payment="solicitorNoticeDetails.monthlyPayment"
					:custom-payment-condition="solicitorNoticeDetails.customCondition"
					:text-dark="false"
				/>
			</BasePlaceHolder>
		</section>
		<LogosList
			v-if="ventureLogos.length > 0"
			class="venture-logos-list"
			:heading="ventureLogosHeading"
			:logos="ventureLogos"
			:background-color="ventureLogosBackground"
			:opaque="true"
			:marquee-duration="'45s'"
			:marquee-gap="'3rem'"
			:marquee-logos-size="'1.75rem'"
		/>
		<VentureCapitalCaseStudy v-if="caseStudyData" :case-study="caseStudyData" />
		<GrowthTechCompanies v-if="hasAssets" :asset-elements="assetElements" />
		<VentureCapitalWhyVenture />
		<VentureCapitalAboutTheFund />
		<VentureCapitalTheFundriseDifference v-if="cards" :section-header="differenceHeader" :cards="cards" />
		<RelatedArticlesSection
			v-if="investorUpdatesSection.relatedArticles.length"
			:heading="investorUpdatesSection.sectionHeading"
			:related-articles="investorUpdatesSection.relatedArticles"
		/>
		<VentureCapitalCTA v-if="!appStore.isAdvisor" />
	</BaseLayout>
</template>
<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, Ref, ref } from 'vue';
import { LandingPageHeader, MarketingImageHero } from 'types/layout';
import { MarketingSiteCard, SolicitorNoticeDetails } from 'types/marketing-site';
import { MediaPartnerNoticeData, SanityMarketingCaseStudy } from 'types/sanity/marketing-components';
import { appCloudfrontPath } from '@constants';
import { Asset } from 'types/assets';
import AssetPageHero from '@components/marketing-site/asset-page-hero.vue';
import { defaultSolicitorNotice } from '@constants/marketing-site';
import { getMarketingPageVentureAssets } from '@api/sanity/proxy/assets';
import { getMediaPartnerNotices } from '@api/sanity/graphql/media-partner-notices';
import { getVentureMarketingPage } from '@api/sanity/graphql/venture-marketing-page';
import GrowthTechCompanies from '@components/offering-details/growth-tech-companies.vue';
import { growthTechReitId } from '@constants/reit';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import LogosList from '@components/marketing-site/logos-list.vue';
import { mapInvestorUpdateArticleTeasers } from '@utils/blog';
import RelatedArticlesSection from '@components/marketing-site/related-articles-section.vue';
import { SanityImage } from 'types/sanity';
import { SanityRelatedArticlesSection } from 'types/sanity/blog-education-center';
import SolicitorNotice from '@components/marketing-site/solicitor-notice.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import VentureCapitalAboutTheFund from '@components/marketing-site/venture-capital/venture-capital-about-the-fund.vue';
import VentureCapitalCaseStudy from '@components/marketing-site/venture-capital/venture-case-study.vue';
import VentureCapitalCTA from '@components/marketing-site/venture-capital/venture-capital-cta.vue';
import { ventureCapitalPresetPlanId } from '@constants/plans-info';
import VentureCapitalTheFundriseDifference from '@components/marketing-site/venture-capital/venture-capital-the-fundrise-difference.vue';
import VentureCapitalWhyVenture from '@components/marketing-site/venture-capital/venture-capital-why-venture.vue';
import { VentureMarketingPage } from 'types/sanity/venture-marketing';

interface Props {
	isPartnerPage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isPartnerPage: false
});

const route = useRoute();
const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const assetElements: Ref<Array<Asset>> = ref([]);
const loading = ref(true);
const solicitorNoticeDetails: Ref<SolicitorNoticeDetails> = ref(defaultSolicitorNotice);

const sanityData: Ref<VentureMarketingPage | null> = ref(null);

const assetHeroConfig: MarketingImageHero = {
	backgroundColor: 'bg-color-content-primary',
	description:
		'We aim to give all investors the opportunity to invest in a portfolio of top-tier private technology companies before they IPO. ',
	heading: 'Venture Capital',
	title: 'Invest in tomorrow’s great tech companies, today',
	imageUrl: `${appCloudfrontPath}/images/marketing-site/hero-images/hero-img-venture-no-labels.png`
};

const leadCaptureFormConfig: LeadCaptureFormConfig = {
	buttonText: 'Sign up',
	signupFlowType: 'PRESET_PLAN',
	presetPlanId: ventureCapitalPresetPlanId,
	thirdPartyAuthTheme: 'SECONDARY'
};

const hasAssets = computed((): boolean => assetElements.value.length > 0);

const investorUpdatesSection = computed((): SanityRelatedArticlesSection => {
	return {
		sectionHeading: sanityData.value?.investorUpdatesSection?.sectionHeading,
		relatedArticles: mapInvestorUpdateArticleTeasers(
			sanityData.value?.investorUpdatesSection?.relatedArticles || []
		)
	};
});

const ventureLogos: Ref<Array<string>> = ref([]);
const ventureLogosHeading = computed((): string => sanityData.value?.ventureLogos?.logosHeading ?? '');
const ventureLogosBackground = computed(
	(): string | undefined => sanityData.value?.ventureLogos?.logosBackgroundColor?.hex ?? undefined
);

const caseStudyData: Ref<SanityMarketingCaseStudy | null> = ref(null);

const utmSource = computed((): string => (route.query.utm_source ? route.query.utm_source.toString() : ''));

onBeforeMount(() => {
	if (props.isPartnerPage) {
		thirdPartyAuthStore.disable(onBeforeUnmount);
	}
});

const differenceHeader = computed((): LandingPageHeader | undefined => {
	const toutsPanel = sanityData.value?.toutsPanel;

	return {
		heading: toutsPanel?.panelHeading || '',
		summary: [toutsPanel?.panelSummary || ''],
		title: toutsPanel?.panelOverline || ''
	};
});

const cards = computed((): Array<MarketingSiteCard> | undefined => {
	const toutsPanel = sanityData.value?.toutsPanel?.panelTouts;
	const marketingCards: Array<MarketingSiteCard> = [];

	if (toutsPanel && toutsPanel?.length > 0) {
		toutsPanel.forEach((tout) => {
			marketingCards.push({
				title: tout.toutHeading || '',
				description: tout.toutSummary || '',
				link:
					tout.toutCta?.url && tout.toutCta?.linkText
						? {
								href: tout.toutCta?.url || undefined,
								target: tout.toutCta?.openInNewTab ? '_blank' : '_self',
								text: tout.toutCta?.linkText || undefined
							}
						: undefined
			});
		});
		return marketingCards;
	}
	return undefined;
});

async function setup(): Promise<void> {
	useHead({
		title: 'Venture Capital',
		meta: [
			{ property: 'og:title', content: 'Venture Capital' },
			{
				name: 'description',
				content: "Invest in tomorrow's great tech companies, today"
			},
			{
				property: 'og:description',
				content: "Invest in tomorrow's great tech companies, today"
			}
		]
	});
	try {
		const [ventureMarketingSanityResponse, assetsResponse] = await Promise.all([
			getVentureMarketingPage(),
			getMarketingPageVentureAssets()
		]);
		sanityData.value = ventureMarketingSanityResponse;

		assetElements.value = assetsResponse;
		sanityData.value = ventureMarketingSanityResponse;

		caseStudyData.value = ventureMarketingSanityResponse?.caseStudyPanel ?? null;

		if (ventureMarketingSanityResponse?.ventureLogos?.logos) {
			const logos = ventureMarketingSanityResponse.ventureLogos.logos.map((logo: SanityImage) => logo.asset.url);
			ventureLogos.value = logos;
		}

		if (props.isPartnerPage) {
			const mediaPartners = await getMediaPartnerNotices();
			const partnerNotices = mediaPartners?.partnerNotices ?? [];
			const solicitorDetails: MediaPartnerNoticeData | null =
				partnerNotices.find((p: MediaPartnerNoticeData) => p.utmSource === utmSource.value) || null;

			const details: SolicitorNoticeDetails = {
				...defaultSolicitorNotice,
				name: solicitorDetails?.name || defaultSolicitorNotice.name,
				payment: solicitorDetails?.payment || defaultSolicitorNotice.payment,
				monthlyPayment: solicitorDetails?.monthlyPayment || defaultSolicitorNotice.monthlyPayment,
				isInvestor: solicitorDetails?.isInvestor || defaultSolicitorNotice.isInvestor,
				isCpaPartner: solicitorDetails?.isCpaPartner || defaultSolicitorNotice.isCpaPartner
			};

			solicitorNoticeDetails.value = details;
		}
	} catch (error) {
	} finally {
		loading.value = false;
	}
}
setup();
</script>
